/**
 * The show styles page (container, D32): the list and the create dialog.
 * "From the template" builds the show style from the one template source
 * (`@norna/playout-engine/studioTemplate.js`'s `showStyleTemplate`, what `acp
 * show-style-template` prints). Creating one needs `device.configure`
 * tenant-wide: a show style belongs to no one studio.
 */
import { showStyleTemplate } from '@norna/playout-engine/studioTemplate.js';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCreateShowStyle, useGrants, useShowStyles } from '../app/hooks.js';
import { CreateShowStyleDialog } from '../components/showStyles/CreateShowStyleDialog.js';
import { ShowStyleList, showStyleHref } from '../components/showStyles/ShowStyleList.js';
import { allows } from '../lib/permissions.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function ShowStylesView() {
  const showStyles = useShowStyles();
  const create = useCreateShowStyle();
  const grants = useGrants();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div className="mx-auto grid max-w-5xl gap-6">
      <ShowStyleList
        showStyles={showStyles.data ?? []}
        loading={showStyles.isFetching}
        error={showStyles.error === null ? null : messageOf(showStyles.error)}
        canCreate={allows(grants, 'device.configure', { siteId: null, studioId: null })}
        onCreate={() => {
          create.reset();
          setOpen(true);
        }}
        onRetry={() => {
          void showStyles.refetch();
        }}
        onOpen={(showStyleId) => {
          void navigate({ to: showStyleHref(showStyleId) });
        }}
      />
      <CreateShowStyleDialog
        open={open}
        onOpenChange={setOpen}
        pending={create.isPending}
        error={create.error === null ? null : messageOf(create.error)}
        onSubmit={(request) => {
          create.mutate(
            { showStyleId: request.showStyleId, name: request.name, config: request.fromTemplate ? showStyleTemplate() : {} },
            {
              onSuccess: (showStyle) => {
                setOpen(false);
                toast.success(`Created “${showStyle.name}”`);
                void navigate({ to: showStyleHref(showStyle.showStyleId) });
              },
            },
          );
        }}
      />
    </div>
  );
}
